/* eslint-disable no-console */
import React from 'react';
import { BrowserClient, Hub, Severity } from '@sentry/browser';
import { FormattedMessage } from 'react-intl-next';
import ErrorIcon from '@atlaskit/icon/core/migration/error';
import { N0, R400 } from '@atlaskit/theme/colors';
import { fireOperationalAnalytics } from '@atlassian/analytics-bridge';
import { isFedRamp } from '@atlassian/atl-context';
import { maskEmailIds } from '@atlassian/proforma-utils';
import { Action, ActionSubject } from '@atlassian/proforma-utils/analytics';
import { Notices } from '../../components/Notice/noticeTypes';
import { Environment } from '../../models';
// structured tags that we send to Sentry
// everything else from details object will go into "extras"
const TAGS = ['module', 'actionSubject', 'url'];
// From https://sentry.io/settings/atlassian-2y/projects/proforma/keys/
const SENTRY_PUBLIC_KEY = 'c6ac6cc7ef554e3286ec09d412c9c1f4';
const SENTRY_PROJECT_ID = '6388218';
const SENTRY_COMMERCIAL_DSN = `https://${SENTRY_PUBLIC_KEY}@o55978.ingest.sentry.io/${SENTRY_PROJECT_ID}`;
const SENTRY_FEDRAMP_DSN = `https://${SENTRY_PUBLIC_KEY}@sentry.atlassian-us-gov-mod.com/${SENTRY_PROJECT_ID}`;
const PROFORMA_SENTRY_PROJECT_DSN = isFedRamp() ? SENTRY_FEDRAMP_DSN : SENTRY_COMMERCIAL_DSN;
export class LiveErrorUtils {
  constructor(moduleName, showFlag, createAnalyticsEvent, environment) {
    this.moduleName = void 0;
    this.showFlag = void 0;
    this.createAnalyticsEvent = void 0;
    this.sentryHub = void 0;
    this.environment = void 0;
    this.moduleName = moduleName;
    this.showFlag = showFlag;
    this.createAnalyticsEvent = createAnalyticsEvent;
    this.environment = environment;
    this.sentryHub = this.getSentryHub(environment);
  }
  reportError(error, options) {
    var _options$actionSubjec;
    const errorPrefix = `Proforma(${this.moduleName}): `;
    const severity = options !== null && options !== void 0 && options.severity ? options.severity : Severity.Error;

    // 1) Log error in console
    switch (severity) {
      case Severity.Warning:
        console.warn(errorPrefix, error);
        break;
      case Severity.Info:
        console.info(errorPrefix, error);
        break;
      default:
        console.error(errorPrefix, error);
        break;
    }
    const actionSubject = (_options$actionSubjec = options === null || options === void 0 ? void 0 : options.actionSubject) !== null && _options$actionSubjec !== void 0 ? _options$actionSubjec : ActionSubject.PROFORMA_UI;
    const attributes = (options === null || options === void 0 ? void 0 : options.attributes) && Object.fromEntries(Object.entries(options.attributes).map(([key, value]) => [key, typeof value === 'string' ? maskEmailIds(value) : value]));

    // Todo: add user agent, package version, url,
    const details = {
      actionSubject,
      errorId: options === null || options === void 0 ? void 0 : options.id,
      severity,
      module: this.moduleName,
      errorMsg: error.message,
      errorInfo: options === null || options === void 0 ? void 0 : options.errorInfo,
      isClientFetchError: error.isClientFetchError,
      requestName: error.requestName,
      traceId: error.traceId,
      status: error.status,
      userAgent: this.getBrowserUserAgent(),
      url: this.getUrl(),
      ...attributes
    };

    // 2) Send error to Atlassian analytics pipeline (GasV3)
    fireOperationalAnalytics(this.createAnalyticsEvent({}), `${actionSubject} ${Action.FAILED}`, details);

    // 3) Send error to Sentry
    if (this.environment === Environment.LOCAL || this.sentryHub === undefined) {
      return;
    }
    const sentryHub = this.sentryHub;
    sentryHub.withScope(scope => {
      scope.setLevel(severity);
      Object.keys(details).forEach(key => {
        const value = details[key];
        if (value) {
          if (TAGS.includes(key)) {
            scope.setTag(key, value.toString());
          } else {
            scope.setExtra(key, value);
          }
        }
      });
      sentryHub.captureException(error);
    });
  }

  /**
   * @private
   * @deprecated notifyUser has been deprecated. Please use the `useFlags()` hook directly instead.
   */
  notifyUser(errorNotice, customDescription) {
    const notice = Notices[errorNotice];
    if (notice) {
      this.showFlag({
        appearance: 'error',
        isAutoDismiss: true,
        //TO DO - https://product-fabric.atlassian.net/browse/DSP-21423
        // eslint-disable-next-line @atlaskit/design-system/no-legacy-icons
        icon: /*#__PURE__*/React.createElement(ErrorIcon, {
          label: "",
          LEGACY_secondaryColor: `var(--ds-icon-danger, ${R400})`,
          color: `var(--ds-icon-inverse, ${N0})`
        }),
        title: /*#__PURE__*/React.createElement(FormattedMessage, notice.title),
        description: customDescription ? customDescription : /*#__PURE__*/React.createElement(FormattedMessage, notice.message)
      });
      return Promise.resolve();
    } else {
      console.error(`Could not show error, notice type ${errorNotice} does not exist`);
      return Promise.reject();
    }
  }
  getSentryHub(environment) {
    try {
      const client = new BrowserClient({
        environment,
        dsn: PROFORMA_SENTRY_PROJECT_DSN
      });
      return new Hub(client);
    } catch (error) {
      const actionSubject = 'getProformaSentryHub';
      const details = {
        actionSubject,
        severity: Severity.Error,
        module: this.moduleName,
        errorMsg: error.message,
        userAgent: this.getBrowserUserAgent(),
        url: this.getUrl()
      };
      fireOperationalAnalytics(this.createAnalyticsEvent({}), `${actionSubject} ${Action.FAILED}`, details);
    }
  }
  getBrowserUserAgent() {
    var _window$navigator$use, _window, _window$navigator;
    return (_window$navigator$use = (_window = window) === null || _window === void 0 ? void 0 : (_window$navigator = _window.navigator) === null || _window$navigator === void 0 ? void 0 : _window$navigator.userAgent) !== null && _window$navigator$use !== void 0 ? _window$navigator$use : 'unknown';
  }
  getUrl() {
    var _window$document$loca, _window2, _window2$document, _window2$document$loc;
    return (_window$document$loca = (_window2 = window) === null || _window2 === void 0 ? void 0 : (_window2$document = _window2.document) === null || _window2$document === void 0 ? void 0 : (_window2$document$loc = _window2$document.location) === null || _window2$document$loc === void 0 ? void 0 : _window2$document$loc.host) !== null && _window$document$loca !== void 0 ? _window$document$loca : 'unknown';
  }
}